import { z } from 'zod';

import { mediaVariant } from './media';

/**
 * Texto rico das noticias e das paginas editaveis (docs/conteudos): o JSON do
 * editor TipTap (ProseMirror) do back-office, reduzido a uma lista fechada de
 * elementos. Nunca ha HTML guardado nem HTML desenhado a partir do texto: o
 * site monta cada elemento em React, por isso nao ha como injetar um script.
 *
 * `normalizeRichDoc` corre na API (ao gravar) e no site (ao mostrar): deita
 * fora tudo o que nao esta na lista, e recusa ligacoes, imagens e videos
 * fora das regras abaixo.
 *
 * Elementos:
 *   - blocos: paragrafo, titulo (nivel 2 ou 3), lista (com ou sem numeros),
 *     citacao, linha divisoria, imagem, video (YouTube ou Twitch);
 *   - texto com negrito, italico e ligacao; quebra de linha.
 */

/**
 * Este pacote nao tem tipos do DOM nem do Node (tsconfig: `types: []`), mas o
 * `URL` global existe nos dois lados. So o que se usa dele.
 */
interface ParsedUrl {
  protocol: string;
  username: string;
  password: string;
  hostname: string;
  pathname: string;
  searchParams: { get(name: string): string | null };
  toString(): string;
}
const WebURL = (globalThis as unknown as { URL: new (input: string) => ParsedUrl }).URL;

export type RichMark =
  { type: 'bold' } | { type: 'italic' } | { type: 'link'; attrs: { href: string } };

export interface RichText {
  type: 'text';
  text: string;
  marks?: RichMark[];
}

export type RichInline = RichText | { type: 'hardBreak' };

export interface RichParagraph {
  type: 'paragraph';
  content?: RichInline[];
}

export interface RichHeading {
  type: 'heading';
  attrs: { level: 2 | 3 };
  content?: RichInline[];
}

export interface RichListItem {
  type: 'listItem';
  content: (RichParagraph | RichList)[];
}

export interface RichList {
  type: 'bulletList' | 'orderedList';
  content: RichListItem[];
}

export interface RichBlockquote {
  type: 'blockquote';
  content: RichParagraph[];
}

export interface RichImage {
  type: 'image';
  /** `src`: uma imagem carregada (`/api/media/...`); `alt` e obrigatorio ao gravar. */
  attrs: { src: string; alt: string };
}

export type EmbedProvider = 'youtube' | 'twitch';

export interface RichEmbed {
  type: 'embed';
  /**
   * YouTube: `id` = id do video (11 caracteres). Twitch: `video/<n>`,
   * `clip/<slug>` ou `channel/<nome>`.
   */
  attrs: { provider: EmbedProvider; id: string };
}

export type RichBlock =
  | RichParagraph
  | RichHeading
  | RichList
  | RichBlockquote
  | RichImage
  | RichEmbed
  | { type: 'horizontalRule' };

export interface RichDoc {
  type: 'doc';
  content: RichBlock[];
}

export const EMPTY_DOC: RichDoc = { type: 'doc', content: [{ type: 'paragraph' }] };

/** Limites de um documento (nos e texto), para nada crescer sem fim. */
export const RICH_MAX_NODES = 5000;
export const RICH_MAX_TEXT = 100_000;

export class RichDocError extends Error {}

// ---------------------------------------------------------------------------
// Ligacoes e videos
// ---------------------------------------------------------------------------

/**
 * Ligacoes aceites: `https://`, `http://`, `mailto:` e caminhos do proprio
 * site (`/loja`). Nada de `javascript:` nem `//outro-site`.
 */
export function safeHref(raw: string): string | null {
  const href = raw.trim();
  if (href.length === 0 || href.length > 2000) return null;
  if (/^\/(?![/\\])/.test(href)) return href;
  if (/^mailto:[^\s@]+@[^\s@]+$/i.test(href)) return href;
  try {
    const url = new WebURL(href);
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
    if (url.username || url.password) return null;
    return url.toString();
  } catch {
    return null;
  }
}

/** A ligacao sai do site? (abre noutro separador, sem `Referer` completo.) */
export const isExternalHref = (href: string) => /^(https?:|mailto:)/i.test(href);

const MEDIA_SRC = /^\/api\/media\/[0-9a-f-]{36}\/(sm|md|lg)\.webp$/;

/** So imagens carregadas no back-office: nada de imagens de terceiros a seguir quem le. */
export function isMediaSrc(src: string): boolean {
  return MEDIA_SRC.test(src);
}

const YOUTUBE_ID = /^[A-Za-z0-9_-]{11}$/;
const TWITCH_ID = /^(video\/\d{1,15}|clip\/[A-Za-z0-9_-]{1,100}|channel\/[A-Za-z0-9_]{3,25})$/;

export function validEmbed(provider: string, id: string): provider is EmbedProvider {
  if (provider === 'youtube') return YOUTUBE_ID.test(id);
  if (provider === 'twitch') return TWITCH_ID.test(id);
  return false;
}

/**
 * O video de um link colado no editor: `youtube.com/watch?v=`, `youtu.be/`,
 * `youtube.com/shorts/`, `twitch.tv/videos/`, `clips.twitch.tv/`,
 * `twitch.tv/<canal>/clip/` ou `twitch.tv/<canal>`.
 */
export function parseEmbedUrl(raw: string): RichEmbed['attrs'] | null {
  let url: ParsedUrl;
  try {
    url = new WebURL(raw.trim());
  } catch {
    return null;
  }
  const host = url.hostname.replace(/^(www\.|m\.)/, '');
  const parts = url.pathname.split('/').filter(Boolean);
  let found: RichEmbed['attrs'] | null = null;
  if (host === 'youtube.com' || host === 'youtube-nocookie.com') {
    const id =
      parts[0] === 'watch'
        ? url.searchParams.get('v')
        : parts[0] === 'shorts' || parts[0] === 'embed' || parts[0] === 'live'
          ? parts[1]
          : null;
    if (id) found = { provider: 'youtube', id };
  } else if (host === 'youtu.be') {
    if (parts[0]) found = { provider: 'youtube', id: parts[0] };
  } else if (host === 'clips.twitch.tv') {
    if (parts[0]) found = { provider: 'twitch', id: `clip/${parts[0]}` };
  } else if (host === 'twitch.tv') {
    if (parts[0] === 'videos' && parts[1]) found = { provider: 'twitch', id: `video/${parts[1]}` };
    else if (parts[1] === 'clip' && parts[2])
      found = { provider: 'twitch', id: `clip/${parts[2]}` };
    else if (parts.length === 1 && parts[0])
      found = { provider: 'twitch', id: `channel/${parts[0]}` };
  }
  return found && validEmbed(found.provider, found.id) ? found : null;
}

/**
 * O endereco do leitor embutido. O Twitch exige `parent` = o dominio da
 * pagina. O YouTube vai pelo dominio sem cookies.
 */
export function embedSrc(embed: RichEmbed['attrs'], parentHost: string): string {
  if (embed.provider === 'youtube') {
    return `https://www.youtube-nocookie.com/embed/${embed.id}`;
  }
  const [kind, id] = embed.id.split('/') as [string, string];
  const parent = `parent=${encodeURIComponent(parentHost)}`;
  if (kind === 'clip') return `https://clips.twitch.tv/embed?clip=${id}&${parent}`;
  if (kind === 'video') return `https://player.twitch.tv/?video=v${id}&${parent}&autoplay=false`;
  return `https://player.twitch.tv/?channel=${id}&${parent}&autoplay=false`;
}

/** O endereco para ver o video no proprio site (YouTube ou Twitch). */
export function embedPageUrl(embed: RichEmbed['attrs']): string {
  if (embed.provider === 'youtube') return `https://www.youtube.com/watch?v=${embed.id}`;
  const [kind, id] = embed.id.split('/') as [string, string];
  if (kind === 'clip') return `https://clips.twitch.tv/${id}`;
  if (kind === 'video') return `https://www.twitch.tv/videos/${id}`;
  return `https://www.twitch.tv/${id}`;
}

// ---------------------------------------------------------------------------
// Normalizar
// ---------------------------------------------------------------------------

type Obj = Record<string, unknown>;
const isObj = (v: unknown): v is Obj => typeof v === 'object' && v !== null && !Array.isArray(v);
const children = (node: Obj): unknown[] => (Array.isArray(node.content) ? node.content : []);
const attr = (node: Obj, key: string): unknown =>
  isObj(node.attrs) ? (node.attrs as Obj)[key] : undefined;

interface Budget {
  nodes: number;
  text: number;
  /** `strict`: ao gravar, uma imagem sem texto alternativo e um erro. */
  strict: boolean;
}

function spend(budget: Budget, text = 0): void {
  budget.nodes += 1;
  budget.text += text;
  if (budget.nodes > RICH_MAX_NODES) throw new RichDocError('o texto e demasiado comprido');
  if (budget.text > RICH_MAX_TEXT) throw new RichDocError('o texto e demasiado comprido');
}

function marksOf(node: Obj): RichMark[] {
  if (!Array.isArray(node.marks)) return [];
  const out: RichMark[] = [];
  for (const mark of node.marks) {
    if (!isObj(mark)) continue;
    if (
      (mark.type === 'bold' || mark.type === 'italic') &&
      !out.some((m) => m.type === mark.type)
    ) {
      out.push({ type: mark.type });
    } else if (mark.type === 'link' && !out.some((m) => m.type === 'link')) {
      const href = attr(mark, 'href');
      const safe = typeof href === 'string' ? safeHref(href) : null;
      // Uma ligacao recusada fica como texto normal; ao gravar, e um erro.
      if (safe !== null) out.push({ type: 'link', attrs: { href: safe } });
    }
  }
  return out;
}

function inlines(node: Obj, budget: Budget): RichInline[] {
  const out: RichInline[] = [];
  for (const child of children(node)) {
    if (!isObj(child)) continue;
    if (child.type === 'text' && typeof child.text === 'string' && child.text.length > 0) {
      spend(budget, child.text.length);
      if (budget.strict) checkLinks(child);
      const marks = marksOf(child);
      const text: RichText = { type: 'text', text: child.text };
      if (marks.length > 0) text.marks = marks;
      out.push(text);
    } else if (child.type === 'hardBreak') {
      spend(budget);
      out.push({ type: 'hardBreak' });
    }
  }
  return out;
}

function checkLinks(node: Obj): void {
  if (!Array.isArray(node.marks)) return;
  for (const mark of node.marks) {
    if (!isObj(mark) || mark.type !== 'link') continue;
    const href = attr(mark, 'href');
    if (typeof href !== 'string' || safeHref(href) === null) {
      throw new RichDocError(`ligacao invalida: ${String(href).slice(0, 80)}`);
    }
  }
}

function withInlines<T extends RichParagraph | RichHeading>(block: T, content: RichInline[]): T {
  if (content.length > 0) block.content = content;
  return block;
}

function paragraph(node: Obj, budget: Budget): RichParagraph {
  spend(budget);
  return withInlines({ type: 'paragraph' }, inlines(node, budget));
}

function list(node: Obj, budget: Budget, depth: number): RichList | null {
  if (depth > 4) throw new RichDocError('listas dentro de listas a mais');
  spend(budget);
  const items: RichListItem[] = [];
  for (const item of children(node)) {
    if (!isObj(item) || item.type !== 'listItem') continue;
    spend(budget);
    const content: (RichParagraph | RichList)[] = [];
    for (const inner of children(item)) {
      if (!isObj(inner)) continue;
      if (inner.type === 'paragraph') content.push(paragraph(inner, budget));
      else if (inner.type === 'bulletList' || inner.type === 'orderedList') {
        const nested = list(inner, budget, depth + 1);
        if (nested) content.push(nested);
      }
    }
    if (content.length === 0 || content[0]?.type !== 'paragraph') {
      content.unshift({ type: 'paragraph' });
    }
    items.push({ type: 'listItem', content });
  }
  if (items.length === 0) return null;
  return { type: node.type === 'orderedList' ? 'orderedList' : 'bulletList', content: items };
}

function block(node: Obj, budget: Budget): RichBlock | null {
  switch (node.type) {
    case 'paragraph':
      return paragraph(node, budget);
    case 'heading': {
      spend(budget);
      const level = attr(node, 'level') === 3 ? 3 : 2;
      return withInlines({ type: 'heading', attrs: { level } }, inlines(node, budget));
    }
    case 'bulletList':
    case 'orderedList':
      return list(node, budget, 1);
    case 'blockquote': {
      spend(budget);
      const content: RichParagraph[] = [];
      for (const inner of children(node)) {
        if (isObj(inner) && inner.type === 'paragraph') content.push(paragraph(inner, budget));
      }
      return content.length > 0 ? { type: 'blockquote', content } : null;
    }
    case 'horizontalRule':
      spend(budget);
      return { type: 'horizontalRule' };
    case 'image': {
      spend(budget);
      const src = attr(node, 'src');
      const alt = attr(node, 'alt');
      if (typeof src !== 'string' || !isMediaSrc(src)) {
        if (budget.strict) throw new RichDocError('so imagens carregadas no back-office');
        return null;
      }
      const altText = typeof alt === 'string' ? alt.trim().slice(0, 300) : '';
      if (budget.strict && altText === '') {
        throw new RichDocError('falta a descricao (texto alternativo) de uma imagem');
      }
      return { type: 'image', attrs: { src: mediaVariant(src, 'lg'), alt: altText } };
    }
    case 'embed': {
      spend(budget);
      const provider = attr(node, 'provider');
      const id = attr(node, 'id');
      if (typeof provider !== 'string' || typeof id !== 'string' || !validEmbed(provider, id)) {
        if (budget.strict) throw new RichDocError('video invalido');
        return null;
      }
      return { type: 'embed', attrs: { provider, id } };
    }
    default:
      return null;
  }
}

/**
 * O documento so com o que e permitido. `strict` (ao gravar) recusa ligacoes
 * e imagens fora das regras em vez de as tirar caladamente, para quem escreve
 * saber o que esta mal.
 */
export function normalizeRichDoc(input: unknown, options: { strict?: boolean } = {}): RichDoc {
  if (!isObj(input) || input.type !== 'doc') throw new RichDocError('documento invalido');
  const budget: Budget = { nodes: 0, text: 0, strict: options.strict ?? false };
  const content: RichBlock[] = [];
  for (const child of children(input)) {
    if (!isObj(child)) continue;
    const b = block(child, budget);
    if (b) content.push(b);
  }
  return { type: 'doc', content: content.length > 0 ? content : [{ type: 'paragraph' }] };
}

/** Para gravar: o documento normalizado, ou um erro de validacao claro. */
export const richDocSchema = z.unknown().transform((value, ctx): RichDoc => {
  try {
    return normalizeRichDoc(value, { strict: true });
  } catch (error) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: error instanceof RichDocError ? error.message : 'documento invalido',
    });
    return z.NEVER;
  }
});

/** O documento que vem da API ja foi validado; aqui so se confirma a forma. */
export const storedRichDocSchema = z.unknown().transform((value): RichDoc => {
  try {
    return normalizeRichDoc(value);
  } catch {
    return EMPTY_DOC;
  }
});

// ---------------------------------------------------------------------------
// Texto simples
// ---------------------------------------------------------------------------

function inlineText(content: RichInline[] | undefined): string {
  return (content ?? []).map((i) => (i.type === 'text' ? i.text : ' ')).join('');
}

function blockText(b: RichBlock | RichListItem): string[] {
  switch (b.type) {
    case 'paragraph':
    case 'heading':
      return [inlineText(b.content)];
    case 'bulletList':
    case 'orderedList':
    case 'listItem':
    case 'blockquote':
      return b.content.flatMap((c) => blockText(c));
    default:
      return [];
  }
}

/** O texto do documento, sem formatacao (resumos, RSS, contagem de palavras). */
export function richDocText(doc: RichDoc): string {
  return doc.content
    .flatMap((b) => blockText(b))
    .map((t) => t.replace(/\s+/g, ' ').trim())
    .filter(Boolean)
    .join('\n\n');
}

/** Corta um texto no fim de uma palavra, com reticencias. */
export function excerpt(text: string, max: number): string {
  const flat = text.replace(/\s+/g, ' ').trim();
  if (flat.length <= max) return flat;
  const cut = flat.slice(0, max - 1);
  const space = cut.lastIndexOf(' ');
  return `${(space > max * 0.6 ? cut.slice(0, space) : cut).replace(/[\s.,;:!?-]+$/, '')}…`;
}

/** Minutos de leitura (200 palavras por minuto), no minimo 1. */
export function readingMinutes(doc: RichDoc): number {
  const words = richDocText(doc).split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / 200));
}
